<template>
  <transition name="fade">
    <div
      v-if="isVisibleCenterMapDraw"
      class="crosshair"
    ></div>
  </transition>
  <div
    class="map-vector__container"
  >
    <div
      ref="mapRef"
      class="map-vector__map"
    ></div>
    <div
      v-if="tooltipContent !== ''"
      :style="tooltipStyle"
      class="help-tooltip"
    >
      {{ tooltipContent }}
    </div>
    <div :class="lateralContainerClasses">
      <MapLayersTreeSelector
        :layers="maplibreLayers"
        :leaf-map-layers="leafMapLayers"
        :legacy-tree="false"
        :exists-custom-layers-settings="existsCustomLayersSettings"
        @set-transparency="setUserTransparency"
        @reset-layers-styles="resetLayersStyles"
      />
      <CalcRoutesControl :route-layers="routeLayers" />
    </div>
    <MapScale
      ref="mapScaleRef"
      :dpc="dpc"
    />
    <MapPrint
      ref="mapPrintRef"
    />
    <MapZoom />
    <MiniMap
      class="map-vector__mini-map"
    />
    <MeasureGridMenu />
    <MapBottom />
  </div>
  <Overlay ref="componentOverlayRef" />
  <div class="map-vector__container-print">
    <div
      ref="printMapRef"
      :class="printMapClasses"
    ></div>
    <MapScale
      ref="printMapScaleRef"
      :class="printMapScaleClasses"
      :dpc="dpc"
      :is-print-scale="true"
    />
  </div>
</template>

<script setup>
import {ref, reactive, onMounted, provide, computed, watch, watchEffect} from 'vue';
import {useStore} from 'vuex';
import {useTimer} from 'vue-timer-hook';
import {Map, View} from 'ol';
import {Group as LayerGroup} from 'ol/layer';
import Collection from 'ol/Collection';
import {MouseWheelZoom} from 'ol/interaction';
import {getPointResolution} from 'ol/proj';
import MapLibreLayer from '@/ol-maplibre-layer';
// Measurement
import {Vector as VectorSource} from 'ol/source';
import {Draw} from 'ol/interaction';
import {defaults as interactionDefaults} from 'ol/interaction/defaults';
import {ScaleLine} from 'ol/control';
import {getLength, getArea} from 'ol/sphere';
import {Vector as VectorLayer} from 'ol/layer';
import {Circle as CircleStyle, Fill, Stroke, Style} from 'ol/style';
import {LineString, Polygon} from 'ol/geom';
import maplibregl from 'maplibre-gl';

import {EXTENT_VIEW_BBOX_3857} from '@/api/util-coordinates';
import {getPhotoById} from '@/api/photo/photo.api';
import {transform} from 'ol/proj';
import {useImmer} from '@sttedra_ui/framework';
import Overlay from './Overlay.vue';
import OLOverlay from 'ol/Overlay.js';
import CalcRoutesControl from '@/components/calc-routes-control/CalcRoutesControl';
import MapScale from '@/components/map/map-scale/MapScale';
import MiniMap from '@/components/map/mini-map/MiniMap';
import MapZoom from '@/components/map/map-zoom/MapZoom';
import MapPrint from '@/components/map/map-print/MapPrint';
import MapBottom from '@/components/map/MapBottom';
import MeasureGridMenu from '@/components/map/MeasureGridMenu';
import {
  getVectorOverviewMaplibreStyle,
  getVectorMaplibreStyle,
  getVectorLayers,
} from '@/api/vector/map-vector-layers/map-vector-layers.api';
import {getLayerDescription} from '@/api/vector/map-vector-layers/description-layer-map';
import LayerApiModel from '@/api/visor/layer/layer.api-model';
import generateTileLayerFromCapabilities from
  '@/composables/generate-tile-layer-capabilities/generate-tile-layer-capabilities';

import MapVectorComponentModel from './map-vector.component-model.js';

import MapLayersTreeSelector from './map-layers-tree-selector/MapLayersTreeSelector';

import {calculateZoomLevel, getScaleFromResolution, dpc2dpi} from '@/util-scale';
// #region Props, Emits
const props___ = defineProps({
  configMap: {
    type: Object,
    default: null,
  },
});
// #endregion

// #region Params, Ref, Reactives, Computed
const store = useStore();
const routeLayers = computed(() => store.state.userLayers.routeLayers);
const markerLayers = computed(() => store.state.userLayers.markerLayers);
const gridLayers = computed(() => store.state.userLayers.gridLayers);
const featureLayers = ref([]);
const componentOverlayRef = ref();
const GROUP_ROUTE_LAYERS = reactive(
  new LayerGroup({
    layers: [],
    properties: {
      name: 'ROUTE_LAYERS',
    },
  }),
);
const GROUP_MARKER_LAYERS = reactive(
  new LayerGroup({
    layers: [],
    properties: {
      name: 'MARKER_LAYERS',
    },
  }),
);
const dpcScreen = computed(() => store.state.map.screenDPC);
const dpcPrint = computed(() => store.state.map.printDPC);
const printMapStartTime = ref(null);
const printing = computed(() => store.state.app.printing);
const printMapClasses = computed(() => {
  const baseClasses = 'map-vector__print-map';
  if (!printing?.value) {
    return baseClasses + ' map-vector__print-map--hidden';
  }
  return baseClasses;
});
const printMapScaleClasses = computed(() => {
  const baseClasses = 'map-vector__print-map-scale';
  if (!printing?.value) {
    return baseClasses + ' map-vector__print-map-scale--hidden';
  }
  return baseClasses;
});
const originalViewResolution = ref(null);
const widthPrint = ref(null);
const heightPrint = ref(null);
const dpc = ref(dpcScreen.value);
const GROUP_GRID_LAYERS = reactive(
  new LayerGroup({
    layers: [],
    properties: {
      name: 'GRID_LAYERS',
    },
  }),
);
const GROUP_MINIMAP_MAPS = reactive(
  new LayerGroup({
    layers: [],
    properties: {
      name: 'MINIMAP_MAPS',
    },
  }),
);
const GROUP_RASTER_LAYERS = reactive(
  new LayerGroup({
    layers: [],
    properties: {
      name: 'RASTER_LAYERS',
    },
  }),
);
const mapPrintRef = ref(null);
const mapScaleRef = ref(null);
const printMapScaleRef = ref(null);
const mapCenter = computed(() => store.state.map.center);
const mapZoom = computed(() => store.state.map.zoom);
const measureMode = computed(() => store.state.map.measureMode);
const centerMapDraw = computed(() => store.state.map.centerMapDraw);
const triggerScale = computed(() => store.state.map.triggerScale);
const mapScaleMeters = computed(() => store.state.map.mapScaleMeters);
const triggerExtent = computed(() => store.state.map.triggerExtent);
const userExtent = computed(() => store.state.map.userExtent);
const triggerPrint = computed(() => store.state.map.triggerPrint);
const layersSettings = computed(() => store.state.layers.layersSettings);
const layersSettingsKeys = computed(
  () => store.getters['layers/layersSettingsKeys'],
);
const getLayerState = computed(() => store.getters['layers/getLayerState']);
const existsCustomLayersSettings = computed(() => {
  return !!layersSettingsKeys?.value?.length;
});
const overlayMinification = computed(() => store.state.app.overlayMinification);
const printDimensions = computed(() => store.state.map.printDimensions);
const modelMap = new MapVectorComponentModel({});
const {ref: modelMapRef, update: updateMapRef} = useImmer(modelMap);
const mapRef = ref();
const printMapRef = ref();
const instanceMapRef = ref();
const instancePrintMapRef = ref();
// const instanceMapScaleRef = ref();
const maplibreRef = ref();
const infoOverlayRef = ref([]);
const maplibreLayers = ref([]);
const lateralContainerClasses = computed(() => {
  let res = 'map-vector__lateral-container gap-2';
  if (!!overlayMinification?.value) {
    res = res + ' map-vector__lateral-container--screen-minimized';
  }
  return res;
});
const getLeafLayers = (node) => {
  let layers = [];
  if (!!node?.children.length) {
    for (const child of node.children) {
      if (!!child?.children?.length) {
        const childLayers = getLeafLayers(child);
        layers = [...layers, ...childLayers];
      }
      if (!child?.children?.length) {
        layers.push(child);
      }
    }
  }
  return layers;
};
const leafMapLayers = computed(() => {
  let layers = [];
  if (!!maplibreLayers.value?.length) {
    layers = getLeafLayers({children: maplibreLayers.value});
  }
  return layers;
});
// Render flags
const olLoaded = ref(false);
const mapLoaded = computed(() => olLoaded?.value);
// Print timeout (seconds)
const PRINTING_TIMEOUT = !!parseInt(STTEDRA_CUSTOM_MAP_TILE_LOADING_TIMEOUT.timeout) ?
  parseInt(STTEDRA_CUSTOM_MAP_TILE_LOADING_TIMEOUT.timeout) : 30;
const printingTimer = ref(null);
const mapPrintingTimeout = ref(false);
// Measurement
const notFirstDrawAux = ref(false);
const content = '';
const tooltipContent = ref(content);
let measuringInteraction;
const finalStyle = new Style({
  stroke: new Stroke({
    color: 'rgb(37, 150, 190)',
    width: 2,
  }),
  image: new CircleStyle({
    radius: 5,
    fill: new Fill({
      color: 'rgba(255, 255, 255, 0.2)',
    }),
    stroke: new Stroke({
      color: 'rgba(0, 0, 0, 0.7)',
    }),
  }),
});
const tooltipStyle = ref({
  position: 'absolute',
  backgroundColor: 'rgba(0, 0, 0, 0.7)',
  color: 'white',
  padding: '7px',
  borderRadius: '10px',
  zIndex: 1000,
  transform: 'translate(-50%, -50%)',
  pointerEvents: 'none',
});
// Controls
function getScaleControl(map) {
  let control = null;
  if (!!map && !!map?.getControls) {
    const controls = map.getControls().getArray();
    for (let i = 0; i < controls.length && !control; i++) {
      const c = controls[i];
      if (c instanceof ScaleLine) {
        control = c;
      }
    }
  }
  return control;
}
const viewScaleControl = computed(() => {
  return getScaleControl(instanceMapRef?.value);
});
const printScaleControl = computed(() => {
  return getScaleControl(instancePrintMapRef?.value);
});

// feature - turistic routes, green ways and camino de santiago on map
const dashArraySequence = [
  [0, 2, 3],
  [0.5, 2, 2.5],
  [1, 2, 2],
  [1.5, 2, 1.5],
  [2, 2, 1],
  [2.5, 2, 0.5],
  [3, 2, 0],
];
const animationState = {
  RutaTuristica: {step: 0, animationId: null, selectedId: null},
  ViaVerde: {step: 0, animationId: null, selectedId: null},
  CaminoDeSantiago: {step: 0, animationId: null, selectedId: null},
};
let animationActive = false;
const isVisibleCenterMapDraw = computed(() => store.state.map.centerMapDraw);
// #endregion

// #region Functions
function createMap(MAP_LIBRE_STYLES, MAP_LIBRE_MINIMAP_STYLES) {
  const maplibreLayer = new MapLibreLayer({
    maplibreOptions: {
      style: MAP_LIBRE_STYLES,
      // preserveDrawingBuffer is
      //   needed to get info from the
      //   canvas HTML element and use it to print
      preserveDrawingBuffer: true,
    },
  });
  const maplibrePrintLayer = new MapLibreLayer({
    maplibreOptions: {
      style: MAP_LIBRE_STYLES,
      // preserveDrawingBuffer is
      //   needed to get info from the
      //   canvas HTML element and use it to print
      preserveDrawingBuffer: true,
    },
  });
  const mapMinimapLayer = new MapLibreLayer({
    maplibreOptions: {
      style: MAP_LIBRE_MINIMAP_STYLES,
    },
  });
  GROUP_MINIMAP_MAPS.setLayers(new Collection([mapMinimapLayer]));
  const view = new View({
    projection: 'EPSG:3857',
    center: store.state.map.center,
    zoom: store.state.map.zoom,
    minZoom: 4,
    maxZoom: 13,
    constrainResolution: false,
    extent: EXTENT_VIEW_BBOX_3857,
  });
  const printView = new View({
    projection: 'EPSG:3857',
    center: store.state.map.center,
    zoom: store.state.map.zoom,
    minZoom: 4,
    maxZoom: 13,
    constrainResolution: false,
    extent: EXTENT_VIEW_BBOX_3857,
  });
  const layers = [maplibreLayer, GROUP_RASTER_LAYERS,
    GROUP_MARKER_LAYERS, GROUP_ROUTE_LAYERS, GROUP_GRID_LAYERS];
  const printLayers = [maplibrePrintLayer];
  const viewInteractions = interactionDefaults({
    altShiftDragRotate: false,
    pinchRotate: false,
    doubleClickZoom: true,
  });
  instanceMapRef.value = new Map({
    view,
    layers,
    target: mapRef.value,
    interactions: viewInteractions,
  });
  maplibreRef.value = maplibreLayer.maplibreMap;

  const printInteractions = interactionDefaults({
    altShiftDragRotate: false,
    onFocusOnly: true,
    doubleClickZoom: false,
    keyboard: false,
    mouseWheelZoom: false,
    shiftDragZoom: false,
    dragPan: false,
    pinchRotate: false,
    pinchZoom: false,
  });
  instancePrintMapRef.value = new Map({
    view: printView,
    layers: printLayers,
    target: printMapRef.value,
    interactions: printInteractions,
  });

  // Change the interaction with the mouseWheelZoom
  instanceMapRef.value.addInteraction(
    new MouseWheelZoom({
      constrainResolution: false,
      duration: 100,
      timeout: 0,
      maxDelta: 1,
      useAnchor: true,
    }),
  );

  // Set events to map
  instanceMapRef.value.on('pointermove', onPointermoveMap);
  instanceMapRef.value.on('moveend', onMoveendMap);
  instanceMapRef.value.on('singleclick', onSingleClick);
  // instancePrintMapRef.value.on('rendercomplete', onOLRenderComplete);
}
function getOpacityPropertyNamesByLayerType(layerType) {
  let opacityPropertyName = [];
  // https://maplibre.org/maplibre-style-spec/layers/#type
  switch (layerType) {
    case 'fill':
      opacityPropertyName = ['fill-opacity'];
      break;
    case 'line':
      opacityPropertyName = ['line-opacity'];
      break;
    case 'symbol':
      opacityPropertyName = ['icon-opacity', 'text-opacity'];
      break;
    case 'circle':
      opacityPropertyName = ['circle-opacity', 'circle-stroke-opacity'];
      break;
    case 'heatmap':
      opacityPropertyName = ['heatmap-opacity'];
      break;
    case 'fill-extrusion':
      opacityPropertyName = ['fill-extrusion-opacity'];
      break;
    case 'raster':
      opacityPropertyName = ['raster-opacity'];
      break;
    case 'background':
      opacityPropertyName = ['background-opacity'];
      break;
    default:
      break;
  }
  return opacityPropertyName;
}
async function resetLayersStyles() {
  if (layersSettings?.value) {
    const promises = [];
    for (const key of layersSettingsKeys.value) {
      const {layerId, opacityPropertyName} = layersSettings.value[key];
      if (opacityPropertyName === 'all') {
        const p = store.dispatch(
          'layers/removeLayerSettings',
          {layerId},
          {root: true},
        );
        promises.push(p);
      } else {
        promises.push(setTransparency(layerId, 0, opacityPropertyName));
      }
    }
    Promise.all(promises);
  }
}
async function setUserTransparency({layerId, transparency}) {
  let layer = null;
  if (leafMapLayers?.value?.length && !!layerId) {
    layer = leafMapLayers.value.find((el) => el.layerName == layerId);
  }
  if (!!layer) {
    let allRelatedLayersIds = [layerId];
    if (!!layer?.selectorLinked?.length) {
      allRelatedLayersIds = [layerId, ...layer.selectorLinked];
    }
    const promises = [];
    for (const lid of allRelatedLayersIds) {
      const layerMaplibreStyle = maplibreRef.value
        .getStyle()
        .layers.find((l) => l.id === lid);
      if (!!layerMaplibreStyle?.type) {
        const opacityPropertyNames = getOpacityPropertyNamesByLayerType(
          layerMaplibreStyle.type,
        );
        for (const opacityPropertyName of opacityPropertyNames) {
          promises.push(
            setTransparency(lid, transparency, opacityPropertyName),
          );
        }
      }
    }
    Promise.all(promises);
    await store.dispatch(
      'layers/setLayerTransparency',
      {layerId, opacityPropertyName: 'all', transparency},
      {root: true},
    ); // For the record
  }
}
async function setTransparency(layerId, transparency, opacityPropertyName) {
  if (!!layersSettings?.value) {
    const layerState = getLayerState.value(layerId, opacityPropertyName);
    const validTransparency = transparency != null && transparency >= 0;
    if (maplibreRef?.value && validTransparency) {
      let originalOpacity = layerState?.originalState?.opacity;
      if (originalOpacity == null) {
        originalOpacity = maplibreRef.value.getPaintProperty(
          layerId,
          opacityPropertyName,
        );
        if (originalOpacity == null) {
          originalOpacity = 1;
        }
        await store.dispatch(
          'layers/saveLayerSettings',
          {layerId, opacityPropertyName, opacity: originalOpacity},
          {root: true},
        );
      }
      const opacityVariation = 1 - transparency;
      let newOpacity = opacityVariation * originalOpacity;
      newOpacity = Math.round(newOpacity * 100) / 100;
      maplibreRef.value.setPaintProperty(
        layerId,
        opacityPropertyName,
        newOpacity,
      );
      await store.dispatch(
        'layers/setLayerTransparency',
        {layerId, opacityPropertyName, transparency},
        {root: true},
      );
      // console.log('id transp: ' + layerId);
      // console.log('trans: ' + transparency);
      // console.log('opacity: ' + newOpacity);
      if (transparency === 0) {
        await store.dispatch(
          'layers/removeLayerSettings',
          {layerId},
          {root: true},
        );
      }
    }
  }
}
// Events ...
/**
 * Fired from event moveend of the map
 *
 */
async function onMoveendMap() {
  const newCenter = instanceMapRef.value.getView().getCenter();
  if (!!newCenter) {
    updateMapRef((draft) => {
      draft.update({
        center: newCenter,
      });
      return draft;
    });
    await store.dispatch(
      'map/setCenter',
      {center: newCenter},
      {root: true},
    );
  }
  const newZoom = instanceMapRef.value.getView().getZoom();
  if (!!newZoom) {
    updateMapRef((draft) => {
      draft.update({
        zoom: newZoom,
      });
      return draft;
    });
    await store.dispatch('map/setZoom', {zoom: newZoom}, {root: true});
  }
}
// TODO: Ordenar por elemento más cercano al closestPoint
function getFeatureInfo(pixel, singleLayerName = null,
  includeNotVisible = false, filterIds = [], closestPoint = null) {
  let features = [];
  let featuresProperties = [];
  if (!!maplibreRef?.value && !!featureLayers?.value?.length) {
    let featureLayerNames = featureLayers.value.map((fl) => fl.layerName);
    if (!!singleLayerName) {
      featureLayerNames = featureLayerNames.filter((fln) => fln === singleLayerName);
    }
    features = maplibreRef.value.queryRenderedFeatures(pixel, {
      layers: featureLayerNames,
    });
    if (!includeNotVisible) {
      features = features.filter((f) => !!f.properties.visibility ||
                                      f.properties?.visibility == null);
    }
    if (!!filterIds?.length) {
      features = features.filter((f) => !filterIds.includes(f.properties.id));
    }
    if (!!closestPoint) {
      const closestPointLngLat =
        new maplibregl.LngLat(closestPoint[0], closestPoint[1]);

      features = features.filter((f) => f.geometry.type === 'Point');
      features.sort((f1, f2) => {
        const p1LngLat =
          new maplibregl.LngLat(f1.geometry.coordinates[0], f1.geometry.coordinates[1]);
        const p2LngLat =
          new maplibregl.LngLat(f2.geometry.coordinates[0], f2.geometry.coordinates[1]);
        const d1 = closestPointLngLat.distanceTo(p1LngLat);
        const d2 = closestPointLngLat.distanceTo(p2LngLat);
        return d1 - d2;
      });
    }
    featuresProperties = features.map((f) => f.properties);
  }
  return {features, featuresProperties};
}
function hasFeatureInfo(pixel) {
  const {features} = getFeatureInfo(pixel);
  return !!features?.length;
}
async function getOverlayInfo(feature, mainInfo = true) {
  let overlayInfo = {};
  if (!!featureLayers?.value?.length && !!feature?.properties) {
    const featureId = feature.properties.id;
    const layerName = feature.layer.id;
    const featureLayer = featureLayers.value.find((fl) => fl.layerName === layerName);
    overlayInfo.layerDescription = feature.layer.metadata.description;
    if (!!featureLayer?.popupDescription) {
      overlayInfo.layerDescription = featureLayer.popupDescription;
    }
    if (!!featureLayer?.materialIcon) {
      overlayInfo.icon = {
        type: 'material-icon',
        icon: featureLayer.materialIcon,
      };
    }
    overlayInfo.fields = [];
    for (const field of featureLayer.fields) {
      if (feature?.properties[field.name] != null) {
        let fieldValue = feature.properties[field.name];
        if (!!field?.isJson) {
          fieldValue = JSON.parse(fieldValue);
        }
        const relatedPromises = [];
        if (!!field?.isRelatedLayerPointList && mainInfo) {
          let i = 0;
          const filterIds = []; // Filtrar features ya recogidas
          for (const coord of fieldValue) {
            const coord3857 = [coord.long, coord.lat];
            const coord4326 = transform(coord3857, 'EPSG:3857', 'EPSG:4326');
            const pixel = maplibreRef.value.project(coord4326);
            fieldValue[i].pixel = pixel;
            const {features: allFeatures} =
              getFeatureInfo(pixel, layerName, true, filterIds, coord4326);
            const features = allFeatures.filter((f) => f.properties.id !== featureId);
            let relatedFeature = null;
            if (!!features?.length) {
              relatedFeature = features[0];
              filterIds.push(relatedFeature.properties.id);
              const p = getOverlayInfo(relatedFeature, false).then((oi) => {
                return {...oi, coord3857, id: relatedFeature?.properties?.id, layerName};
              });
              relatedPromises.push(p);
            }
            i++;
          }
        }
        const relatedOverlayInfoList = await Promise.all(relatedPromises);
        let url = null;
        let imageName = '';
        if (field?.fieldType === 'imageUrl') {
          const {name, photoUrl} = await getPhotoById(fieldValue);
          url = photoUrl;
          imageName = name;
        }
        const isTitle = !!field?.isTitle ? true : false;
        overlayInfo.fields.push({
          description: field.description,
          fieldType: field?.fieldType,
          value: fieldValue,
          url,
          imageName,
          isTitle,
          relatedOverlayInfoList,
        });
      }
    }
    const coord3857 = !!feature?.geometry?.coordinates?.length ?
      transform(feature.geometry.coordinates,
        'EPSG:4326', 'EPSG:3857') : [];
    overlayInfo = {...overlayInfo, coord3857, id: featureId, layerName};
  }
  return overlayInfo;
}
/**
 * Fired from event singleclick from map
 *
 * @param {Event} event
 *
 */
async function onSingleClick(event) {
  await store.dispatch('userLayers/cleanMarkerLayers');
  componentOverlayRef.value.setPosition(null);
  infoOverlayRef.value = [];
  let {features} = getFeatureInfo(event.pixel);

  // turistic routes and green ways feature
  let selectedIdRuta = -1;
  let selectedIdViaVerde = -1;
  let selectedIdCaminoSantiago = -1;

  if (!!features?.length) {
    let centerFeature = null;
    let firstFeature = null;
    features.forEach((f) => {
      if (!firstFeature) {
        firstFeature = f;
      }
      if (!centerFeature) {
        centerFeature = event.coordinate;
        if (f?._geometry?.type === 'Point') {
          // TODO: centerFeature debería estar en 3857
          centerFeature = transform(f._geometry.coordinates, 'EPSG:4326', 'EPSG:3857');
        }
      }
      infoOverlayRef.value.push(f.properties);
    });
    if (!!firstFeature) {
      const info = await getOverlayInfo(firstFeature);
      infoOverlayRef.value = info;
      setTimeout(() => {
        componentOverlayRef.value.setPosition(centerFeature);
      }, 100);
    }

    features = removeDuplicates(features);
    // stop previous animation if exist
    if (animationActive) {
      stopAnimation('RutaTuristica');
      stopAnimation('ViaVerde');
      stopAnimation('CaminoDeSantiago');
    }
    // turistic routes, green ways and camino de santiago feature
    features.forEach((feature) => {
      if (feature.sourceLayer === 'RutaTuristica') {
        const defaultColor = '#7FFFFF';
        maplibreRef.value.setPaintProperty('RutaTuristica', 'line-color', defaultColor);
        selectedIdRuta = features[0].properties.id;
        startAnimation('RutaTuristica', selectedIdRuta);
      }
      if (feature.sourceLayer === 'ViaVerde') {
        const defaultColorViaVerde = '#6DB241';
        maplibreRef.value.setPaintProperty('ViaVerde', 'line-color', defaultColorViaVerde);
        selectedIdViaVerde = features[0].properties.id;
        startAnimation('ViaVerde', selectedIdViaVerde);
      }
      if (feature.sourceLayer === 'CaminoDeSantiago') {
        const defaultColorCaminoSantiago = '#7520c1';
        maplibreRef.value
          .setPaintProperty('CaminoDeSantiago', 'line-color', defaultColorCaminoSantiago);
        selectedIdCaminoSantiago = features[0].properties.id;
        startAnimation('CaminoDeSantiago', selectedIdCaminoSantiago);
      }
    });
  } else {
    // stop animations when clicking out
    if (animationActive) {
      stopAnimation('RutaTuristica');
      stopAnimation('ViaVerde');
      stopAnimation('CaminoDeSantiago');
    }
  }
  return;
}
function removeDuplicates(arr) {
  const arrAux = [];
  const arrAuxIds = [];
  arr.forEach((item) => {
    if (!arrAuxIds.includes(item.properties.id)) {
      arrAuxIds.push(item.properties.id);
      arrAux.push(item);
    }
  });
  return arrAux;
}
function startAnimation(layer, id) {
  animationState[layer].selectedId = id;
  animationActive = true;
  animationState[layer].animationId = requestAnimationFrame((timestamp) =>{
    maplibreRef.value.setPaintProperty(layer, 'line-width', [
      'case',
      ['==', ['get', 'id'], id], 4,
      2,
    ]);
    maplibreRef.value.setPaintProperty(layer, 'line-opacity', [
      'case',
      ['==', ['get', 'id'], id], 1,
      0,
    ]);
    // Esta capa no se anima porque será la que se vea estática mientras
    // una sola línea de la capa normal se anima (y las demás se ocultan)
    maplibreRef.value.setPaintProperty(layer + 'NonAnimated', 'line-opacity', [
      'case',
      ['==', ['get', 'id'], id], 0,
      1,
    ]);
    animateDashArray(layer, timestamp, id);
  });
}

function stopAnimation(layer) {
  const state = animationState[layer];
  if (state.animationId !== null) {
    cancelAnimationFrame(state.animationId);
    state.animationId = null;
    state.selectedId = null;
  }
  maplibreRef.value.setPaintProperty(layer, 'line-width', 4);
  maplibreRef.value.setPaintProperty(layer, 'line-opacity', 1);
  maplibreRef.value.setPaintProperty(layer, 'line-dasharray', [1, 1]);
  maplibreRef.value.setPaintProperty(layer + 'NonAnimated', 'line-opacity', 0);
  animationActive = false;
}
function animateDashArray(layer, timestamp, id) {
  const state = animationState[layer];
  const newStep = parseInt((timestamp / 50) % dashArraySequence.length);
  if (newStep !== state.step) {
    maplibreRef.value.setPaintProperty(layer, 'line-dasharray',
      dashArraySequence [newStep],
    );

    state.step = newStep;
  }

  state.animationId = requestAnimationFrame((timestamp) =>
    animateDashArray(layer, timestamp, id),
  );
}
/**
 * Fired from event pointermove from map
 *
 * @param {Event} event
 *
 */
function onPointermoveMap(event) {
  // measure tooltip
  const coordinate = event.coordinate;
  const pixel = instanceMapRef.value.getPixelFromCoordinate(coordinate);
  tooltipStyle.value.left = `${pixel[0]}px`;
  tooltipStyle.value.top = `${pixel[1]}px`;
  // end measure tooltip
  updateMapRef((draft) => {
    draft.update({positionMouse: event.coordinate});
    return draft;
  });
  // Check for feature info
  if (!!instanceMapRef?.value) {
    const hasFeature = hasFeatureInfo(event.pixel);
    if (hasFeature) {
      instanceMapRef.value.getTargetElement().style.cursor = 'pointer';
    } else {
      instanceMapRef.value.getTargetElement().style.cursor = '';
    }
  }
}
/**
 * Method to change and manage measures
 *
 */
function onChangeMeasureMode(mode) {
  // Remove previous interactions and overlays
  if (measuringInteraction) {
    instanceMapRef.value.removeInteraction(measuringInteraction);
    measuringInteraction = null;
  }
  tooltipContent.value = '';

  // Clear previous drawings
  const layers = instanceMapRef.value.getLayers().getArray();
  for (const layer of layers) {
    if (layer instanceof VectorLayer) {
      layer.getSource().clear();
    }
  }
  const measurementLayer = new VectorLayer({
    source: new VectorSource(),
  });
  instanceMapRef.value.addLayer(measurementLayer);

  // delete previous draw (if exists)
  if (notFirstDrawAux.value) {
    instanceMapRef.value.getInteractions().pop();
  }
  notFirstDrawAux.value = true;
  // reset tooltip content
  tooltipContent.value = '';

  // Clear all overlays (measurement tooltips)
  instanceMapRef.value.getOverlays().clear();

  // If mode is 0, exit early (no drawing at all)
  if (mode === 0) {
    notFirstDrawAux.value = false;
    instanceMapRef.value.getInteractions().pop();
    return;
  }
  // no draw action when mode 0
  if (mode !== 0) {
    tooltipContent.value = 'Haga clic en el mapa para comenzar a medir';
    measuringInteraction = new Draw({
      source: measurementLayer.getSource(),
      style: new Style({
        fill: new Fill({
          color: 'rgba(255, 255, 255, 0.2)',
        }),
        stroke: new Stroke({
          color: 'rgba(0, 0, 0, 0.5)',
          lineDash: [10, 10],
          width: 2,
        }),
        image: new CircleStyle({
          radius: 5,
          stroke: new Stroke({
            color: 'rgba(0, 0, 0, 0.7)',
          }),
          fill: new Fill({
            color: 'rgba(255, 255, 255, 0.2)',
          }),
        }),
      }),
      type: mode === 1 ? 'LineString' : 'Polygon',
    });
    instanceMapRef.value.addInteraction(measuringInteraction);
    measuringInteraction.on('drawstart', (event) => {
      event.feature.on('change', (evt) => updateMeasure(evt));
    });

    measuringInteraction.on('drawend', (event) => {
      event.feature.setStyle(finalStyle);

      const measureTooltipElement = document.createElement('div');
      measureTooltipElement.innerHTML = tooltipContent.value;
      const measureTooltip = new OLOverlay({
        // element: measureTooltipElement,
        element: measureTooltipElement,
        offset: [0, -15],
        positioning: 'center-center',
        stopEvent: false,
        insertFirst: false,
        position: event.feature.getGeometry().getLastCoordinate(),
        className: 'ol-tooltip-static',
      });

      instanceMapRef.value.addOverlay(measureTooltip);
      tooltipContent.value = 'Haga clic en el mapa para seguir midiendo';
    });
  }
}

// set tooltip content everytime you move, including tooltip move
function updateMeasure(event) {
  const geometry = event.target.getGeometry();
  let output;
  if (geometry instanceof LineString) {
    const length = getLength(geometry);
    output = length > 1000 ? `${(length / 1000).toFixed(2)} km` : `${length.toFixed(2)} m`;
  } else if (geometry instanceof Polygon) {
    const area = getArea(geometry);
    output = area > 10000 ? `${(area / 1000000).toFixed(2)} km²` : `${area.toFixed(2)} m²`;
  }
  tooltipContent.value = output;
  const lastCoordinate = geometry.getLastCoordinate();
  const pixel = instanceMapRef.value.getPixelFromCoordinate(lastCoordinate);
  tooltipStyle.value.left = `${pixel[0]}px`;
  tooltipStyle.value.top = `${pixel[1]}px`;
}

/**
 * Prepare the structure of the Tree
 *
 * @param {Array} options
 */
function decorateNode(n) {
  if (!n.selectorShown) {
    return;
  }
  // Primevue Tree node additional properties
  // https://primevue.org/tree/#api.treenode
  n.key = n.id;
  n.label = n.description;
  if (!n?.layerName && !!n?.children?.length) {
    n.leaf = false; // https://primevue.org/tree/#api.treenode
    n.type = 'group';
    n.children = n.children.filter((c) => !!c?.selectorShown);
    for (const l of n.children) {
      decorateNode(l);
    }
  }

  if (!!n?.layerName && !n?.children?.length) {
    const BASE_URL = props___.configMap.urlS3 + '/LegendGraphic/ICON_NAME.png';
    n.leaf = true; // https://primevue.org/tree/#api.treenode
    const descriptionFromMap = getLayerDescription(n.id); // TODO: Quitar con la traducción
    // TODO: Quitar con la traducción
    const newDescription = !!descriptionFromMap ?
      descriptionFromMap :
      n.description;
    n.description = newDescription;
    n.label = n.description; // TODO: Quitar con la traducción
    n.legend = '';
    n.type = 'layer';
    if (!!n?.iconName) {
      n.legend = BASE_URL.replaceAll('ICON_NAME', n.iconName);
    }
    n.iconImgUrl = n.legend;
  }
}
function setLayersTree(options) {
  // Deep copy
  const newOptions = JSON.parse(JSON.stringify(options));
  for (const o of newOptions) {
    decorateNode(o);
  }
  return newOptions;
}
// TODO: Refactor with views/Map.vue
function prepareRasterStructureLayer(layer) {
  let alternativeRequest = null;
  if (layer.alternativeRequest) {
    alternativeRequest = {
      url:
        layer.alternativeRequest?.protocol?.toLowerCase() === 'tms' ?
          layer.getUrlRequestAlternative() :
          layer.getUrlCapabilitiesAlternative(),
      layer:
        (layer.alternativeRequest?.workspace ?
          `${layer.alternativeRequest?.workspace}:` :
          '') + layer.alternativeRequest?.name,
      format: layer.alternativeRequest?.protocol,
      params: {
        FORMAT: layer.alternativeRequest?.imageFormat,
      },
      visible: false,
      values: layer,
      opacity: layer.opacity ? layer.opacity / 100 : 1,
      minZoom: layer.zoomMax - 1 ?? 0,
      maxZoom: 30,
      crossOrigin: layer.alternativeRequest?.crossOrigin,
    };
  }
  return {
    url:
      layer.request?.protocol?.toLowerCase() === 'tms' ?
        layer.getUrlRequest() :
        layer.getUrlCapabilities(),
    layer:
      (layer.request?.workspace ? `${layer.request?.workspace}:` : '') +
      layer.request?.name,
    format: layer.request?.protocol,
    params: {
      FORMAT: layer.request?.imageFormat,
    },
    visible: layer.default,
    values: layer,
    opacity: layer.opacity ? layer.opacity / 100 : 1,
    minZoom: layer.zoomMin ? layer.zoomMin - 1 : 0,
    maxZoom: layer.zoomMax ?? 20,
    crossOrigin: layer.request?.crossOrigin,
    legend: layer.getUrlLegend(),
    alternativeRequest,
  };
}
async function createRasterLayers(rasterLayers) {
  for (const rl of rasterLayers) {
    rl.rasterLayer = new LayerApiModel(rl.rasterInfo);
  }
  const layersStructured = rasterLayers.map((rl) => {
    return prepareRasterStructureLayer(rl.rasterLayer);
  });
  localStorage.removeItem('capabilities');
  const layersGroup = layersStructured.map(async (layer) => {
    return await generateTileLayerFromCapabilities(layer);
  });
  const tileLayers = await Promise.all(layersGroup);
  GROUP_RASTER_LAYERS.setLayers(new Collection(tileLayers.filter((l) => l)));
}
// #endregion

// #region Effects, Hooks
onMounted(async () => {
  const PREVIEW_PREFIX = STTEDRA_CUSTOM_PREVIEW_PREFIX;
  const previewSuffix = window.location.host.startsWith(PREVIEW_PREFIX) ? 'Preview' : '';

  const MAP_LIBRE_STYLES = await getVectorMaplibreStyle(
    props___.configMap.urlS3,
    props___.configMap.urlTileService,
    previewSuffix,
  );
  const MAP_LIBRE_MINIMAP_STYLES = await getVectorOverviewMaplibreStyle(
    props___.configMap.urlS3,
    props___.configMap.urlTileService,
    previewSuffix,
  );
  const {layerTree, rasterLayers, featureLayers: flayers} =
    getVectorLayers(MAP_LIBRE_STYLES);
  featureLayers.value = flayers;
  maplibreLayers.value = setLayersTree(layerTree);
  createMap(MAP_LIBRE_STYLES, MAP_LIBRE_MINIMAP_STYLES);
  // Measurement
  onChangeMeasureMode(measureMode.value);
  await createRasterLayers(rasterLayers);
  watchEffect(async () => {
    if (!!printingTimer.value?.isExpired &&
       !!printing?.value) {
      mapPrintingTimeout.value = true;
      await onRenderComplete();
    }
  });
});
watch(measureMode, (newMeasureMode) => {
  onChangeMeasureMode(newMeasureMode);
});
watch(centerMapDraw, (newCenterMapDraw) => {
  isVisibleCenterMapDraw.value = newCenterMapDraw;
});
watch(
  routeLayers,
  (newRouteLayers) => {
    const layerArray = newRouteLayers.flatMap((ul) => ul.olLayers);
    GROUP_ROUTE_LAYERS.setLayers(new Collection(layerArray));
  },
  {deep: true},
);
watch(
  markerLayers,
  (newMarkerLayers) => {
    const layerArray = newMarkerLayers.flatMap((ul) => ul.olLayers);
    GROUP_MARKER_LAYERS.setLayers(new Collection(layerArray));
  },
  {deep: true},
);
watch(
  gridLayers,
  (newGridLayers) => {
    const layerArray = newGridLayers.map((ul) => ul.olLayer);
    GROUP_GRID_LAYERS.setLayers(new Collection(layerArray));
  },
  {deep: true},
);
watch(mapCenter, (newCenter) => {
  if (!!newCenter && !!instanceMapRef?.value) {
    instanceMapRef.value.getView().setCenter(newCenter);
  }
});
watch(mapZoom, (newZoom) => {
  if (!!newZoom && !!instanceMapRef?.value) {
    instanceMapRef.value.getView().setZoom(newZoom);
  }
});
// Scale trigger
watch([triggerScale, mapScaleMeters], async ([newTrigger, newScaleMeters]) => {
  if (!!newTrigger && !!newScaleMeters && instanceMapRef?.value) {
    const map = instanceMapRef.value;
    const newZoom = calculateZoomLevel(map, newScaleMeters, dpcScreen.value);
    if (!!newZoom) {
      await store.dispatch('map/setZoom', {zoom: newZoom}, {root: true});
    }
  }
});
// Zoom trigger
watch([triggerExtent, userExtent], async ([newTrigger, newExtent]) => {
  if (!!newTrigger && !!newExtent?.length) {
    const map = instanceMapRef.value;
    map.getView().fit(newExtent, map.getSize());
  }
});
/* eslint-disable */
async function onOLRenderComplete(_evt) {
  if (!!printing?.value) {
    olLoaded.value = true;
    await onRenderComplete();
  }
}
/* eslint-enable */
//  Render complete
async function onRenderComplete() {
  const mapReady = !!mapLoaded?.value ||
                   !!mapPrintingTimeout?.value;
  if (!!mapReady &&
     !!mapPrintRef?.value &&
     !!mapScaleRef?.value &&
     !!printMapScaleRef?.value &&
     !!instancePrintMapRef?.value &&
     !!printing?.value &&
     !!viewScaleControl?.value &&
     !!printScaleControl?.value &&
     !!originalViewResolution?.value &&
     !!printDimensions?.value?.length &&
     !!widthPrint?.value && !!heightPrint?.value
  ) {
    olLoaded.value = false;
    mapPrintingTimeout.value = false;
    printingTimer.value = null;
    const dims = [printDimensions.value[0], printDimensions.value[1]];
    const printPromise = mapPrintRef.value.printMap(widthPrint.value, heightPrint.value, dims, 0.7);
    printPromise.then(async (blob) => {
      const timeFinish = new Date();
      const timeStart = printMapStartTime.value;
      await store.dispatch('reports/completeReportBlob', {blob, timeStart, timeFinish});
    }).finally(() => {
      printMapStartTime.value = null;
      store.dispatch('app/endPrinting');
    });
  }
}
// Print trigger
watch(triggerPrint, async (newTrigger) => {
  if (!!newTrigger &&
      !!mapPrintRef?.value &&
      !!instanceMapRef?.value &&
      !!instancePrintMapRef?.value &&
      !!viewScaleControl?.value &&
      !!printScaleControl?.value &&
      !!dpcPrint?.value &&
      !!dpcScreen?.value
  ) {
    // set map scale
    const now = new Date();
    printMapStartTime.value = now;
    const name = 'Mapa PDF'; // TODO: Más detalles
    await store.dispatch('reports/addReportUrlRequest', {time: now, name});
    // await store.dispatch('reports/addReportBlobRequest', {time: now, name});
    const viewMap = instanceMapRef.value;
    const printMap = instancePrintMapRef.value;
    const dims = printDimensions.value;
    // meters by pixel
    const viewResolution = viewMap.getView().getResolution();
    originalViewResolution.value = viewResolution;
    const scale = getScaleFromResolution(viewResolution, dpcScreen.value);
    const dpmm = dpcPrint.value / 10; // dots per mm
    // Image dimensions for printing
    const width = Math.round(dims[0] * dpmm);
    const height = Math.round(dims[1] * dpmm);
    const pointResolution = getPointResolution(
      viewMap.getView().getProjection(),
      dpmm,
      viewMap.getView().getCenter(),
    );
    const newResolution = (scale / 1000) / pointResolution;
    // Set print view
    printMap.getView().setCenter(viewMap.getView().getCenter());
    printMap.getView().setZoom(viewMap.getView().getZoom());
    printScaleControl.value.setDpi(dpc2dpi(dpcPrint.value));
    widthPrint.value = width;
    heightPrint.value = height;
    await store.dispatch('app/beginPrinting');
    printMap.getTargetElement().style.width = width + 'px';
    printMap.getTargetElement().style.height = height + 'px';
    printMap.updateSize();
    printMap.getView().setResolution(newResolution);
    const time = new Date();
    time.setSeconds(time.getSeconds() + PRINTING_TIMEOUT);
    printingTimer.value = useTimer(time);
  }
});
// #endregion

// #region Provide, Expose
provide('modelMapRef', modelMapRef);
provide('instanceMapRef', instanceMapRef);
provide('instancePrintMapRef', instancePrintMapRef);
provide('printMapScaleRef', printMapScaleRef);
provide('maplibreRef', maplibreRef);
provide('infoOverlayRef', infoOverlayRef);
provide('GROUP_MINIMAP_MAPS', GROUP_MINIMAP_MAPS);
provide('GROUP_RASTER_LAYERS', GROUP_RASTER_LAYERS);
provide('GROUP_ROUTE_LAYERS', GROUP_ROUTE_LAYERS);
provide('GROUP_MARKER_LAYERS', GROUP_MARKER_LAYERS);
provide('GROUP_GRID_LAYERS', GROUP_GRID_LAYERS);
// #endregion
</script>

<style lang="scss" scoped>
@import "src/styles";
.map-vector {
  &__container {
    width: 100%;
    height: 100%;
  }
  &__mini-map {
    display: none;
    @include max-screen-height($brand__breakpoint--sm) {
      display: none !important;
    }
    @include min-screen-width($brand__breakpoint--md) {
      display: block;
    }
  }
  &__map, &__print-map {
    width: 100%;
    height: 100%;
  }
  &__print-map {
    position: absolute;
    top: 100dvh;
    left: 0;
    width: 100dvw;
    height: 100dvh;
    &--hidden, &-scale--hidden {
      display: none;
    }
  }
  &__lateral-container {
    position: absolute;
    z-index: 1;
    top: 0;
    right: 0;
    padding: 10px;
    height: auto;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-content: center;
    &--screen-minimized {
      z-index: 2 !important;
    }
  }
}
.tooltip {
  margin-top: 1.5%;
  margin-left: 45%;
  margin-right: 45%;
  display: block;
  text-align: center;
  position: absolute;
  cursor: default;
  opacity: 1;
  font-weight: bold;
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 7px;
  border-radius: 10px;
  z-index: 1000;
}
.help-tooltip {
  margin-top: 1.5%;
  display: block;
  text-align: center;
  position: absolute;
  cursor: default;
  opacity: 1;
  font-weight: bold;
  background-color: rgba(0, 0, 0, 0.9);
  color: white;
  padding: 7px;
  border-radius: 10px;
  z-index: 1000;
}

.floatingMenu {
  position: absolute;
  top: 12rem;
  left: 0.8rem;
  // transform: translateX(-50%);
  @media only screen and (max-width: 1200px) {
    top: 13rem;
  }
  @media only screen and (max-width: 1000px) {
    top: 14rem;
  }
  @media only screen and (max-width: 768px) {
    top: 1rem;
  }
}

.floatingButton {
  width: 40px;
  height: 40px;
  border-radius: 20%;
  background-color: $brand__color--blue !important;
  color: white;
  border: 2px solid white;
  margin-bottom: 10px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.3);
  outline: none;
}
.floatingButton.active {
  background-color: yellow !important;
  color: black;

  .material-icons {
    color: black !important;
  }
}
.crosshair {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 25px;
  height: 25px;
  background: transparent;
  transform: translate(-50%, -50%);
  pointer-events: none;
  z-index: 9999;
  opacity: 0;
  animation: fadeIn 0.2s ease-in-out forwards;
}
.crosshair::before,
.crosshair::after {
  content: '';
  position: absolute;
  background: black;
  opacity: 0;
  animation: fadeIn 0.2s ease-in-out forwards;
}
.crosshair::before {
  left: 50%;
  top: 0;
  height: 100%;
  width: 2px;
  transform: translateX(-50%);
}
.crosshair::after {
  top: 50%;
  left: 0;
  width: 100%;
  height: 1px;
  transform: translateY(-50%);
}

@keyframes fadeIn {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}

@keyframes fadeOut {
  0% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.2s;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}

[title] {
  position: relative;
  cursor: pointer;
}
</style>

// Estilo sin scoped para tooltip de OpenLayers
<style lang="scss">
.ol-tooltip-static {
  //subir unos píxeles en el eje Y para que no se superponga con otro elemento del mapa
  position: absolute;
  top: -4px;
  background: rgba(0, 0, 0, 0.7);
  border-radius: 4px;
  color: white;
  padding: 8px;
  white-space: nowrap;
  font-size: 12px;
  z-index: 9999;
}
//Piquito inferior del tooltip
.ol-tooltip-static:before {
  border-top: 6px solid rgba(0, 0, 0, 0.7);
  border-right: 6px solid transparent;
  border-left: 6px solid transparent;
  content: "";
  position: absolute;
  bottom: -6px;
  margin-left: -7px;
  left: 50%;
}
.ol-tooltip-static:before {
  border-top-color: rgba(0, 0, 0, 0.7);
}
</style>
